import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  APP_SHORTCUT_NEW_SERVICE_CALL,
  APP_SHORTCUT_SAVE_SERVICE_CALL,
  dispatchAppShortcut,
} from '../constants/appKeyboardShortcuts';

export type AppKeyboardShortcutOptions = {
  /** When false, Alt+N and Ctrl/Cmd+S are disabled (e.g. external_viewer). */
  canEditServiceCalls?: boolean;
};

/**
 * Global keyboard shortcuts.
 * Uses e.code (physical key) so Hebrew keyboard layout still works.
 * Mount once under the authenticated layout (with Router).
 */
export function useAppKeyboardShortcuts(
  enabled = true,
  options: AppKeyboardShortcutOptions = {},
): void {
  const navigate = useNavigate();
  const location = useLocation();
  const canEditServiceCalls = options.canEditServiceCalls !== false;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;

      const code = String(e.code || '');

      // Ctrl+S / Cmd+S — שמירת קריאה
      if ((e.ctrlKey || e.metaKey) && !e.altKey && code === 'KeyS') {
        if (!canEditServiceCalls) return;
        e.preventDefault();
        dispatchAppShortcut(APP_SHORTCUT_SAVE_SERVICE_CALL);
        return;
      }

      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      // Alt+S — כל הקריאות (פיזי S, גם במקלדת עברית)
      // תמיד מנווטים עם clear — גם אם כבר בעמוד, כדי לאפס סינון מקוביית דשבורד
      if (code === 'KeyS') {
        e.preventDefault();
        navigate('/ServiceCallsPage', { state: { clearServiceCallsFilters: true } });
        return;
      }

      if (code === 'KeyD') {
        e.preventDefault();
        if (location.pathname !== '/' && location.pathname !== '/dashboard') {
          navigate('/dashboard');
        }
        return;
      }

      if (code === 'KeyN') {
        if (!canEditServiceCalls) return;
        e.preventDefault();
        if (location.pathname === '/ServiceCallsPage') {
          dispatchAppShortcut(APP_SHORTCUT_NEW_SERVICE_CALL);
        } else {
          navigate('/ServiceCallsPage', { state: { openNewCall: true } });
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, canEditServiceCalls, location.pathname, navigate]);
}
